import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumb, EventDateList, EventStatusType, InventoryItem, SiteRoute } from '../SiteClasses';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { bindClick, joinUrls } from '../SiteUtil';
import API from '../API';
import { Button, Header, Icon, Image, Input, Modal } from 'react-frontier';
import classNames from 'classnames';
import moment from 'moment';
import SearchCoreModal from '../components/SearchCoreModal';

var BreadcrumbsContext = createContext<{
	breadcrumbs: Breadcrumb[],
	sidebar: JSX.Element,
	setTitle: (title: string)=>void,
	showSearch: ()=>void,
	showInventorySearch: ()=>void,
}>(null);

var useBreadcrumbs = ()=>useContext(BreadcrumbsContext);

var BreadcrumbsProvider = (props: PropsWithChildren & { routes: SiteRoute[], suffix?: string, })=>{
	var location = useLocation();
	var navigate = useNavigate();
	var [titleHistory, setTitleHistory] = useState<{ pathname: string, title: string }[]>([]);
	var [searchModal, setSearchModal] = useState<boolean>(false);
	var [searchValue, setSearchValue] = useState<string>('');
	var [searchEvents, setSearchEvents] = useState<EventDateList[]>(null);
	var [searchTimeout, setSearchTimeout] = useState(null);
	var [searchError, setSearchError] = useState<string>(null);
	var searchRef = useRef<HTMLInputElement>(null);

	var [inventorySearchModal, setInventorySearchModal] = useState<boolean>(false);
	var [inventorySearchResetKey, setInventorySearchResetKey] = useState<number>(0);

	var closeInventorySearch = ()=>{
		setInventorySearchModal(false);
		setInventorySearchResetKey(a=>a+1);
	};

	useEffect(()=>{
		var suffix = props.suffix || 'AREMA';
		var title = titleHistory.find(a=>a.pathname===location.pathname);
		if(!title || !title.title){
			document.title = suffix;
		}else{
			document.title = `${title.title} - ${suffix}`;
		}
	}, [location, titleHistory]);

	var changeTitle = (title: string)=>{
		if(!location.key) return;
		var hs = [...titleHistory];
		hs = hs.filter(a=>a.pathname!==location.pathname);
		hs.push({ pathname: location.pathname, title });
		setTitleHistory(hs);
	}

	var getBreadcumbUrl = (params: { [x: string]: any }, url: string)=>{
		for(var i in params){
			url = url.replace(new RegExp(`:${i}`, 'gi'), params[i]);
		}
		return url;
	}

	var isCurrentUrl = (location: string, path: string)=>{
		var paths = path.split('/').filter(a=>(!!a && a.length>0));
		var reg = new RegExp(`/${paths.map(p=>{
			if(p.startsWith(':')){
				return `(?<${p.substring(1)}>[A-z0-9_]+)`
			}else{
				return p;
			}
		}).join('/')}/?`)
		var reg_res = reg.exec(location);
		return reg_res;
	}

	var getBreadcrumb = (location: string, parent: string, routes: SiteRoute[]) : Breadcrumb[]=>{
		var bread : Breadcrumb[] = [];
		for(var i of routes){
			var path = joinUrls(parent, i.path);
			var reg_res = isCurrentUrl(location, path);

			if(reg_res){
				if(!i.ignore){
					bread.push({
						text: typeof i.title==='string' ? i.title : i.title(reg_res.groups),
						url: getBreadcumbUrl(reg_res.groups, path),
						sidebar: i.sidebar,
						iconName: i.iconName,
					})
				}
				if(i.children){
					bread.push(...getBreadcrumb(location, path, i.children));
				}
				break;
			}
		}
		return bread;
	}

	var getSidebar = (location: string, parent: string, routes: SiteRoute[])=>{
		var sidebar: JSX.Element;
		for(var i of routes){
			var path = joinUrls(parent, i.path);
			var is_current = !!isCurrentUrl(location, path);
			if(!!is_current){
				if(i.sidebar) sidebar = i.sidebar;
				if(i.children){
					var ch_side = getSidebar(location, path, i.children);
					if(!!ch_side) sidebar = ch_side;
				}
			}
		}
		return sidebar;
	}

	var breadcrumbs = useMemo(()=>{
		if(props.routes){
			return getBreadcrumb(location.pathname, '/', props.routes);
		}else return [];
	}, [location?.pathname]);

	var sidebar = useMemo(()=>{
		if(props.routes){
			return getSidebar(location.pathname, '/', props.routes);
		}
		return null;
	}, [location.pathname]);;

	var onSearchChange = (val: string)=>{
		setSearchValue(val);
		if(searchTimeout){
			clearTimeout(searchTimeout)
			setSearchTimeout(null);
		}

		if(val.length<3){
			return setSearchEvents(null);
		}

		setSearchTimeout(setTimeout(()=>{
			API.searchEvents(val, 0, 30).then(res=>{
				if(res.error) return setSearchError(res.message);
				setSearchEvents(res.data);
			}).catch(err=>{
				return setSearchError('Hubo un error ineseprado realizando la busqueda (LCL-SREVT-1)');
			})
		}, 500));
	}

	var searchInventoryItems = (query: string, page: number, pageSize: number)=>{
		var normalizedQuery = query?.trim() ?? '';
		return API.searchItems(normalizedQuery, page * pageSize, pageSize);
	};

	return <BreadcrumbsContext.Provider value={{
		breadcrumbs,
		setTitle: changeTitle,
		sidebar,
		showSearch: ()=>{
			setTimeout(()=>{
				searchRef.current?.focus();
			}, 50);
			setSearchModal(true);
		},
		showInventorySearch: ()=>{
			setInventorySearchModal(true);
		},
	}}>
		{props.children}
		<Modal open={searchModal} onClose={bindClick(setSearchModal)} size='tiny' className='search-events'>
			<Input ref={searchRef} onChange={onSearchChange} value={searchValue} placeholder='Buscar eventos...' icon='search' />
			<Modal.Content>
				{(searchError || searchValue.length<3) ? (
					<div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
						<Header text={searchError ? 'Error' : 'Buscar eventos'} subtext={searchError || 'Ingresa el nombre o ID del evento para buscar'} iconName={searchError ? 'face-frown-open' : 'search'} iconStyle={{ fontSize: 60, lineHeight: '60px', marginBottom: 15 }} />
					</div>
				) : searchEvents ? (
				searchEvents.map(a=>{
				return <Link to={`/events/${a.date_id}`} onClick={bindClick(setSearchModal)} className={classNames("ar event small", {
					hidden: a.status_id!==null &&
							a.status_id!==EventStatusType.OPERATING &&
							a.status_id!==EventStatusType.PROMOTER_OPERATED &&
							a.status_id!==EventStatusType.POSTPONED &&
							a.status_id!==EventStatusType.CANCELLED
				})}>
						<div className="image">
							<Image src={a.poster_url || '/placeholder.png'} className='image' fallback='/placeholder.png' />
							</div>
							<div className="data">
								<div className="name">{a.event_name}</div>
								<div className="info">
									<div className="meta">
										<Icon name='calendar' /> {moment.unix(a.date).format('DD/MMM/YY HH:mm')}
									</div>
									<div className="city">
										<Icon name='map' /> {a.city}
									</div>
								</div>
							</div>
						</Link>
					})
				) : (
					<div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
						<Header loading text={'Buscando eventos'} />
					</div>
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' onClick={bindClick(setSearchModal)} />
			</Modal.Actions>
		</Modal>
		<SearchCoreModal
			key={inventorySearchResetKey}
			open={inventorySearchModal}
			onClose={closeInventorySearch}
			title='Buscar equipos'
			searchPlaceholder='Buscar por ID, nombre, serie, modelo o tipo'
			onSelected={(item: InventoryItem, setLoading)=>{
				setLoading(true);
				navigate(`/item/${item.item_id}`);
				closeInventorySearch();
				setLoading(false);
			}}
			searchData={searchInventoryItems}
			pageSize={10}
			table={[
				{ name: 'ID', key: 'item_id', collapsing: true },
				{
					name: 'Equipo',
					key: 'type_name',
					render: (val, item: InventoryItem) => (<><Icon name={item.type_icon || 'box-open'} /> {val || `Equipo ${item.type_id}`}</>),
				},
				{ name: 'Nombre', key: 'item_name' },
				{ name: 'Serie', key: 'serial_number' },
				{ name: 'Modelo', key: 'model' },
			]}
		/>
	</BreadcrumbsContext.Provider>
}

export {
	useBreadcrumbs,
	BreadcrumbsProvider,
};
